import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { moviesAPI, smartRatingsAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Save, Plus, Trash2, Search, X, Loader } from 'lucide-react';

const TMDB_KEY = process.env.REACT_APP_TMDB_API_KEY;
const TMDB_BASE = 'https://api.themoviedb.org/3';
const TMDB_IMG = 'https://image.tmdb.org/t/p/w500';

const AdminMovieForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [smartRatingOptions, setSmartRatingOptions] = useState([]);

  // TMDb arama state
  const [tmdbQuery, setTmdbQuery] = useState('');
  const [tmdbResults, setTmdbResults] = useState([]);
  const [tmdbSearching, setTmdbSearching] = useState(false);
  const [tmdbFilling, setTmdbFilling] = useState(false);
  const [showTmdbResults, setShowTmdbResults] = useState(false);
  const searchRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    original_title: '',
    poster: '',
    header_image: '',
    show_in_slider: false,
    trailer: '',
    trailers: [],
    synopsis: '',
    duration: '',
    genres: '',
    formats: [],
    rating: 'Genel İzleyici Kitlesi İçin Uygundur',
    smartRatings: [],
    release_date: '',
    director: '',
    cast: '',
    second_release_date: '',
    distributor: '',
    producer: '',
    country: '',
    importer: '',
    status: 'now_playing',
  });

  useEffect(() => {
    loadSmartRatings();
    if (id) loadMovie(id);
  }, [id]);

  // Dışarı tıklanınca sonuçları kapat
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowTmdbResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadSmartRatings = async () => {
    try {
      const response = await smartRatingsAPI.getAll();
      setSmartRatingOptions(response.data);
    } catch (error) {
      console.error('Error loading smart ratings:', error);
    }
  };

  const loadMovie = async (movieId) => {
    try {
      setLoading(true);
      const response = await moviesAPI.getById(movieId);
      const movie = response.data;
      setFormData({
        title: movie.title || '',
        original_title: movie.original_title || '',
        poster: movie.poster || '',
        header_image: movie.header_image || '',
        show_in_slider: movie.show_in_slider || false,
        trailer: movie.trailer || '',
        trailers: movie.trailers || [],
        synopsis: movie.synopsis || '',
        duration: movie.duration?.toString() || '',
        genres: Array.isArray(movie.genres) ? movie.genres.join(', ') : '',
        formats: movie.formats || [],
        rating: movie.rating || 'Genel İzleyici Kitlesi İçin Uygundur',
        smartRatings: movie.smartRatings || [],
        release_date: convertToISODate(movie.release_date) || '',
        director: movie.director || '',
        cast: movie.cast || '',
        second_release_date: convertToISODate(movie.second_release_date) || '',
        distributor: movie.distributor || '',
        producer: movie.producer || '',
        country: movie.country || '',
        importer: movie.importer || '',
        status: movie.status || 'now_playing',
      });
    } catch (error) {
      toast({ title: 'Hata', description: 'Film yüklenemedi', variant: 'destructive' });
      navigate('/admin/movies');
    } finally {
      setLoading(false);
    }
  };

  // TMDb'de film ara
  const searchTmdb = async () => {
    if (!tmdbQuery.trim()) return;
    setTmdbSearching(true);
    setShowTmdbResults(true);
    try {
      const res = await fetch(
        `${TMDB_BASE}/search/movie?api_key=${TMDB_KEY}&query=${encodeURIComponent(tmdbQuery)}&language=tr-TR&page=1`
      );
      const data = await res.json();
      setTmdbResults(data.results?.slice(0, 6) || []);
    } catch (err) {
      toast({ title: 'Hata', description: 'TMDb araması başarısız', variant: 'destructive' });
    } finally {
      setTmdbSearching(false);
    }
  };

  // Seçilen filmin detaylarını çek ve formu doldur
  const fillFromTmdb = async (movie) => {
    setTmdbFilling(true);
    setShowTmdbResults(false);
    try {
      // Film detayları + credits (oyuncu/yönetmen) - Türkçe
      const [detailRes, creditsRes, trailersRes] = await Promise.all([
        fetch(`${TMDB_BASE}/movie/${movie.id}?api_key=${TMDB_KEY}&language=tr-TR`),
        fetch(`${TMDB_BASE}/movie/${movie.id}/credits?api_key=${TMDB_KEY}&language=tr-TR`),
        fetch(`${TMDB_BASE}/movie/${movie.id}/videos?api_key=${TMDB_KEY}&language=tr-TR`),
      ]);

      const detail = await detailRes.json();
      const credits = await creditsRes.json();
      const videosData = await trailersRes.json();

      // Türkçe synopsis boşsa İngilizce'ye düş
      let synopsis = detail.overview || '';
      if (!synopsis) {
        const enRes = await fetch(`${TMDB_BASE}/movie/${movie.id}?api_key=${TMDB_KEY}&language=en-US`);
        const enDetail = await enRes.json();
        synopsis = enDetail.overview || '';
      }

      // Yönetmen
      const director = credits.crew?.find(c => c.job === 'Director')?.name || '';

      // Oyuncular (ilk 6)
      const cast = credits.cast?.slice(0, 6).map(c => c.name).join(', ') || '';

      // Türler (Türkçe)
      const genreMap = {
        28: 'Aksiyon', 12: 'Macera', 16: 'Animasyon', 35: 'Komedi',
        80: 'Suç', 99: 'Belgesel', 18: 'Drama', 10751: 'Aile',
        14: 'Fantastik', 36: 'Tarih', 27: 'Korku', 10402: 'Müzik',
        9648: 'Gizem', 10749: 'Romantik', 878: 'Bilim Kurgu',
        10770: 'TV Filmi', 53: 'Gerilim', 10752: 'Savaş', 37: 'Kovboy'
      };
      const genres = detail.genres?.map(g => genreMap[g.id] || g.name).join(', ') || '';

      // Yapım ülkeleri
      const countryMap = {
        US: 'ABD', GB: 'İngiltere', FR: 'Fransa', DE: 'Almanya', IT: 'İtalya',
        ES: 'İspanya', TR: 'Türkiye', JP: 'Japonya', KR: 'Güney Kore', CN: 'Çin',
        CA: 'Kanada', AU: 'Avustralya', IN: 'Hindistan', RU: 'Rusya', BR: 'Brezilya',
        MX: 'Meksika', NL: 'Hollanda', BE: 'Belçika', SE: 'İsveç', NO: 'Norveç',
        DK: 'Danimarka', FI: 'Finlandiya', PL: 'Polonya', AT: 'Avusturya', CH: 'İsviçre',
        IE: 'İrlanda', PT: 'Portekiz', GR: 'Yunanistan', CZ: 'Çekya', HU: 'Macaristan',
        RO: 'Romanya', UA: 'Ukrayna', IL: 'İsrail', EG: 'Mısır', ZA: 'Güney Afrika',
        NZ: 'Yeni Zelanda', AR: 'Arjantin', CL: 'Şili', CO: 'Kolombiya', TH: 'Tayland',
        ID: 'Endonezya', MY: 'Malezya', PH: 'Filipinler', VN: 'Vietnam', SG: 'Singapur',
        HK: 'Hong Kong', TW: 'Tayvan', SA: 'Suudi Arabistan', AE: 'Birleşik Arap Emirlikleri',
        IS: 'İzlanda', LU: 'Lüksemburg', BG: 'Bulgaristan', HR: 'Hırvatistan', RS: 'Sırbistan',
        SK: 'Slovakya', SI: 'Slovenya', LT: 'Litvanya', LV: 'Letonya', EE: 'Estonya',
      };
      const country = detail.production_countries?.map(c => countryMap[c.iso_3166_1] || c.name).join(', ') || '';

      // Yapımcı şirket
      const producer = detail.production_companies?.map(c => c.name).slice(0, 2).join(', ') || '';

      // Poster
      const poster = detail.poster_path ? `${TMDB_IMG}${detail.poster_path}` : '';

      // Backdrop (header image)
      const header_image = detail.backdrop_path ? `https://image.tmdb.org/t/p/original${detail.backdrop_path}` : '';

      // Fragmanlar (YouTube)
      const trailers = videosData.results
        ?.filter(v => v.site === 'YouTube' && (v.type === 'Trailer' || v.type === 'Teaser'))
        .slice(0, 3)
        .map((v, i) => ({
          title: v.name || `Fragman ${i + 1}`,
          url: `https://www.youtube.com/watch?v=${v.key}`,
          order: i,
        })) || [];

      // Vizyon tarihi
      const release_date = detail.release_date || '';

      setFormData(prev => ({
        ...prev,
        title: detail.title || movie.title || '',
        original_title: detail.original_title || '',
        poster,
        header_image,
        synopsis,
        duration: detail.runtime?.toString() || '',
        genres,
        release_date,
        director,
        cast,
        country,
        producer,
        trailers,
      }));

      setTmdbQuery('');
      toast({ title: '✅ Bilgiler dolduruldu', description: 'TMDb\'den veriler çekildi. Kontrol edip kaydedin.' });
    } catch (err) {
      console.error(err);
      toast({ title: 'Hata', description: 'Film bilgileri çekilemedi', variant: 'destructive' });
    } finally {
      setTmdbFilling(false);
    }
  };

  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return isoDate;
      const monthNames = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
      return `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
    } catch { return isoDate; }
  };

  const convertToISODate = (turkishDate) => {
    if (!turkishDate) return '';
    if (turkishDate.includes('-')) return turkishDate;
    try {
      const map = {'Ocak':'January','Şubat':'February','Mart':'March','Nisan':'April','Mayıs':'May','Haziran':'June','Temmuz':'July','Ağustos':'August','Eylül':'September','Ekim':'October','Kasım':'November','Aralık':'December'};
      let d = turkishDate;
      Object.keys(map).forEach(k => { d = d.replace(k, map[k]); });
      const date = new Date(d);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch { return ''; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const data = {
        ...formData,
        duration: parseInt(formData.duration),
        genres: formData.genres.split(',').map(g => g.trim()),
        release_date: formatDateForDisplay(formData.release_date),
        second_release_date: formData.second_release_date ? formatDateForDisplay(formData.second_release_date) : '',
      };
      if (id) {
        await moviesAPI.update(id, data);
        toast({ title: 'Başarılı', description: 'Film güncellendi' });
      } else {
        await moviesAPI.create(data);
        toast({ title: 'Başarılı', description: 'Film eklendi' });
      }
      navigate('/admin/movies');
    } catch (error) {
      toast({ title: 'Hata', description: error.response?.data?.detail || 'Film kaydedilemedi', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <p className="text-gray-400">Yükleniyor...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button onClick={() => navigate('/admin/movies')} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800">
            <ArrowLeft size={20} className="mr-2" /> Geri Dön
          </Button>
        </div>

        <h1 className="text-3xl font-bold text-white mb-8">
          {id ? 'Film Düzenle' : 'Yeni Film Ekle'}
        </h1>

        {/* TMDb Arama Kutusu */}
        {!id && (
          <div className="bg-gray-900 border border-yellow-600/40 rounded-lg p-6 mb-6" ref={searchRef}>
            <div className="flex items-center gap-2 mb-3">
              <Search size={18} className="text-[#FDB714]" />
              <h2 className="text-white font-semibold">TMDb'den Otomatik Doldur</h2>
              <span className="text-gray-500 text-sm">(opsiyonel)</span>
            </div>
            <p className="text-gray-400 text-sm mb-4">Film adını aratın, bilgiler otomatik dolsun. Sonra üzerinde değişiklik yapabilirsiniz.</p>

            <div className="flex gap-2">
              <Input
                value={tmdbQuery}
                onChange={(e) => setTmdbQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchTmdb())}
                placeholder="Film adı yazın... (Türkçe veya orijinal)"
                className="bg-gray-800 border-gray-700 text-white flex-1"
              />
              <Button
                type="button"
                onClick={searchTmdb}
                disabled={tmdbSearching || !tmdbQuery.trim()}
                className="bg-[#FDB714] hover:bg-[#e5a510] text-black"
              >
                {tmdbSearching ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
                <span className="ml-2">Ara</span>
              </Button>
            </div>

            {/* Arama Sonuçları */}
            {showTmdbResults && (
              <div className="mt-3 bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                {tmdbSearching ? (
                  <div className="p-4 text-center text-gray-400">Aranıyor...</div>
                ) : tmdbResults.length === 0 ? (
                  <div className="p-4 text-center text-gray-400">Sonuç bulunamadı</div>
                ) : (
                  tmdbResults.map((movie) => (
                    <button
                      key={movie.id}
                      type="button"
                      onClick={() => fillFromTmdb(movie)}
                      disabled={tmdbFilling}
                      className="w-full flex items-center gap-3 p-3 hover:bg-gray-700 transition-colors border-b border-gray-700 last:border-0 text-left"
                    >
                      {movie.poster_path ? (
                        <img
                          src={`https://image.tmdb.org/t/p/w92${movie.poster_path}`}
                          alt={movie.title}
                          className="w-10 h-14 object-cover rounded flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-14 bg-gray-600 rounded flex-shrink-0 flex items-center justify-center">
                          <span className="text-gray-400 text-xs">?</span>
                        </div>
                      )}
                      <div>
                        <div className="text-white font-medium text-sm">{movie.title}</div>
                        {movie.original_title !== movie.title && (
                          <div className="text-gray-400 text-xs">{movie.original_title}</div>
                        )}
                        <div className="text-gray-500 text-xs mt-1">
                          {movie.release_date?.split('-')[0] || '—'}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {tmdbFilling && (
              <div className="mt-3 flex items-center gap-2 text-yellow-400 text-sm">
                <Loader size={14} className="animate-spin" />
                TMDb'den bilgiler çekiliyor...
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-800 rounded-lg p-8 space-y-6">

          {/* Poster önizleme */}
          {formData.poster && (
            <div className="flex gap-4 items-start p-4 bg-gray-800 rounded-lg border border-gray-700">
              <img src={formData.poster} alt="Poster" className="w-24 h-36 object-cover rounded" />
              <div>
                <p className="text-white font-medium">{formData.title}</p>
                {formData.original_title && <p className="text-gray-400 text-sm">{formData.original_title}</p>}
                {formData.duration && <p className="text-gray-400 text-sm mt-1">{formData.duration} dk</p>}
                {formData.genres && <p className="text-gray-400 text-sm">{formData.genres}</p>}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, poster: '', header_image: '' }))}
                  className="text-red-400 text-xs mt-2 hover:text-red-300 flex items-center gap-1"
                >
                  <X size={12} /> Temizle
                </button>
              </div>
            </div>
          )}

          <div>
            <Label className="text-white" htmlFor="title">Film Adı *</Label>
            <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="bg-gray-800 border-gray-700 text-white" required />
          </div>

          <div>
            <Label className="text-white" htmlFor="original_title">Orijinal Adı</Label>
            <Input id="original_title" value={formData.original_title} onChange={(e) => setFormData({ ...formData, original_title: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: The Godfather" />
          </div>

          <div>
            <Label className="text-white" htmlFor="poster">Poster URL *</Label>
            <Input id="poster" value={formData.poster} onChange={(e) => setFormData({ ...formData, poster: e.target.value })} className="bg-gray-800 border-gray-700 text-white" required />
          </div>

          <div>
            <Label className="text-white" htmlFor="header_image">Header Görsel URL (Slider için)</Label>
            <Input id="header_image" value={formData.header_image} onChange={(e) => setFormData({ ...formData, header_image: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Anasayfa slider'ında gösterilecek geniş görsel" />
          </div>

          <div className="flex items-center gap-3">
            <input type="checkbox" id="show_in_slider" checked={formData.show_in_slider} onChange={(e) => setFormData({ ...formData, show_in_slider: e.target.checked })} className="w-4 h-4" />
            <Label htmlFor="show_in_slider" className="text-white cursor-pointer">Anasayfa Slider'ında Göster</Label>
          </div>

          <div>
            <Label className="text-white" htmlFor="trailer">Trailer URL (YouTube) - Eski Format</Label>
            <Input id="trailer" value={formData.trailer} onChange={(e) => setFormData({ ...formData, trailer: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="https://youtube.com/watch?v=..." />
            <p className="text-gray-500 text-xs mt-1">Not: Eski format. Yeni filmler için aşağıdaki "Film Fragmanları" bölümünü kullanın.</p>
          </div>

          {/* Film Fragmanları */}
          <div className="border-t border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Label className="text-white text-lg">Film Fragmanları (Maks 5)</Label>
                <p className="text-gray-500 text-sm mt-1">Birden fazla fragman ekleyebilirsiniz</p>
              </div>
              <Button
                type="button"
                onClick={() => {
                  if (formData.trailers.length >= 5) {
                    toast({ title: 'Limit Aşıldı', description: 'Maksimum 5 fragman ekleyebilirsiniz', variant: 'destructive' });
                    return;
                  }
                  setFormData({ ...formData, trailers: [...formData.trailers, { title: '', url: '', order: formData.trailers.length }] });
                }}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={formData.trailers.length >= 5}
              >
                <Plus size={16} className="mr-2" /> Fragman Ekle
              </Button>
            </div>

            {formData.trailers.length === 0 ? (
              <div className="text-center py-8 bg-gray-800 border border-gray-700 rounded-lg">
                <p className="text-gray-400">Henüz fragman eklenmedi</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.trailers.map((trailer, index) => (
                  <div key={index} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 bg-[#FDB714] text-black w-8 h-8 rounded-full flex items-center justify-center font-bold">{index + 1}</div>
                      <div className="flex-grow space-y-3">
                        <div>
                          <Label className="text-white text-sm">Fragman Başlığı</Label>
                          <Input value={trailer.title} onChange={(e) => { const t = [...formData.trailers]; t[index].title = e.target.value; setFormData({ ...formData, trailers: t }); }} className="bg-gray-900 border-gray-600 text-white mt-1" placeholder="Örn: Resmi Fragman" />
                        </div>
                        <div>
                          <Label className="text-white text-sm">YouTube URL</Label>
                          <Input value={trailer.url} onChange={(e) => { const t = [...formData.trailers]; t[index].url = e.target.value; setFormData({ ...formData, trailers: t }); }} className="bg-gray-900 border-gray-600 text-white mt-1" placeholder="https://youtube.com/watch?v=..." />
                        </div>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => { const t = formData.trailers.filter((_, i) => i !== index); t.forEach((x, i) => x.order = i); setFormData({ ...formData, trailers: t }); }} className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white flex-shrink-0">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-white" htmlFor="synopsis">Açıklama *</Label>
            <Textarea id="synopsis" value={formData.synopsis} onChange={(e) => setFormData({ ...formData, synopsis: e.target.value })} className="bg-gray-800 border-gray-700 text-white min-h-[120px]" required />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white" htmlFor="duration">Süre (dakika) *</Label>
              <Input id="duration" type="number" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} className="bg-gray-800 border-gray-700 text-white" required />
            </div>
            <div>
              <Label className="text-white" htmlFor="status">Durum *</Label>
              <select id="status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white h-10">
                <option value="now_playing">Vizyonda</option>
                <option value="upcoming">Yakında</option>
                <option value="archived">Arşiv</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-white" htmlFor="genres">Türler (virgülle ayırın) *</Label>
            <Input id="genres" value={formData.genres} onChange={(e) => setFormData({ ...formData, genres: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: Aksiyon, Macera, Gerilim" required />
          </div>

          <div>
            <Label className="text-white">Film Formatları</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
              {['2D', '3D', 'IMAX', 'Türkçe Dublaj', 'Türkçe Altyazılı', 'Türkçe'].map((format) => (
                <label key={format} className="flex items-center gap-2 text-white cursor-pointer">
                  <input type="checkbox" checked={formData.formats.includes(format)} onChange={(e) => { if (e.target.checked) { setFormData({ ...formData, formats: [...formData.formats, format] }); } else { setFormData({ ...formData, formats: formData.formats.filter(f => f !== format) }); } }} className="w-4 h-4 rounded" />
                  <span className="text-sm">{format}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-white" htmlFor="release_date">Vizyon Tarihi *</Label>
            <Input id="release_date" type="date" value={formData.release_date} onChange={(e) => setFormData({ ...formData, release_date: e.target.value })} className="bg-gray-800 border-gray-700 text-white [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert" required />
          </div>

          <div>
            <Label className="text-white">Akıllı İşaretler</Label>
            <div className="space-y-2 bg-gray-800 border border-gray-700 rounded-md p-4 max-h-80 overflow-y-auto mt-2">
              {smartRatingOptions.map((rating) => (
                <label key={rating._id} className="flex items-start gap-3 text-white cursor-pointer hover:bg-gray-700/50 p-2 rounded transition-colors">
                  <input type="checkbox" checked={(formData.smartRatings || []).includes(rating._id)} onChange={(e) => { const cur = formData.smartRatings || []; if (e.target.checked) { setFormData({ ...formData, smartRatings: [...cur, rating._id] }); } else { setFormData({ ...formData, smartRatings: cur.filter(r => r !== rating._id) }); } }} className="w-4 h-4 rounded mt-1" />
                  <div className="flex items-center gap-2">
                    {rating.icon && <img src={rating.icon} alt={rating.label} className="w-6 h-6 object-contain" />}
                    <span className="text-sm">{rating.label}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-700 pt-6 mt-6">
            <h3 className="text-lg font-semibold text-white mb-4">Film Detay Bilgileri (Opsiyonel)</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white" htmlFor="director">Yönetmen</Label>
                  <Input id="director" value={formData.director} onChange={(e) => setFormData({ ...formData, director: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: Christopher Nolan" />
                </div>
                <div>
                  <Label className="text-white" htmlFor="country">Ülke</Label>
                  <Input id="country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: ABD, İngiltere" />
                </div>
              </div>
              <div>
                <Label className="text-white" htmlFor="cast">Oyuncular</Label>
                <Input id="cast" value={formData.cast} onChange={(e) => setFormData({ ...formData, cast: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: Leonardo DiCaprio, Tom Hardy" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white" htmlFor="producer">Yapımcı</Label>
                  <Input id="producer" value={formData.producer} onChange={(e) => setFormData({ ...formData, producer: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: Warner Bros" />
                </div>
                <div>
                  <Label className="text-white" htmlFor="distributor">Sinema Dağıtım</Label>
                  <Input id="distributor" value={formData.distributor} onChange={(e) => setFormData({ ...formData, distributor: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: UIP Filmcilik" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white" htmlFor="importer">İthalatçı</Label>
                  <Input id="importer" value={formData.importer} onChange={(e) => setFormData({ ...formData, importer: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: Warner Bros" />
                </div>
                <div>
                  <Label className="text-white" htmlFor="second_release_date">2. Vizyon Tarihi</Label>
                  <Input id="second_release_date" type="date" value={formData.second_release_date} onChange={(e) => setFormData({ ...formData, second_release_date: e.target.value })} className="bg-gray-800 border-gray-700 text-white [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-800">
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 flex-1">
              <Save size={20} className="mr-2" />
              {loading ? 'Kaydediliyor...' : (id ? 'Güncelle' : 'Kaydet')}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/admin/movies')} className="border-gray-700 text-gray-300 hover:bg-gray-800">
              İptal
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminMovieForm;
